import { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

import useNotifications from './useNotifications';
import { logout } from '../redux/authSlice';
import { useDispatch } from 'react-redux';

// Estetään useat samanaikaiset 403-navigaatiot
let forbiddenRedirectPending = false;

export function useApi(
  path,
  {
    initialState = null,
    fetchOnMount = true,
    query = null,
    onError = null
  } = {}
) {
  const [data, setData] = useState(initialState);
  const [loading, setLoading] = useState(fetchOnMount);
  const [error, setError] = useState(null);
  const isMounted = useRef(false);
  const { notify } = useNotifications();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (!error) return;
    if (onError) {
      onError(error);
      return;
    }
    notify({
      content:
        typeof error === 'string'
          ? error
          : error.key || error.message || 'server.error',
      variant: 'danger',
      localize: !!error.key || typeof error === 'string'
    });
  }, [error]);

  const setLocalData = useCallback((value) => {
    if (isMounted.current) {
      setData(value);
    }
  }, []);

  const setLocalError = useCallback((value) => {
    if (isMounted.current) {
      setError(value);
    }
  }, []);

  const request = useCallback(
    async (req) => {
      try {
        setLoading(true);
        setError(null);
        const resolvedPath = req.path || path;
        const url = resolvedPath?.startsWith('http')
          ? resolvedPath
          : `/api/${resolvedPath.replace(/^\/+/, '')}`;

        const response = await axios({
          url,
          method: req.method || 'GET',
          params: req?.params || query,
          ...req
        });

        return response;
      } catch (error) {
        if (isMounted.current) {
          if (error.response?.status === 401) {
            dispatch(logout());
          } else if (error.response?.status === 403) {
            if (!forbiddenRedirectPending) {
              forbiddenRedirectPending = true;
              navigate('/error?key=forbidden');
              setTimeout(() => { forbiddenRedirectPending = false; }, 2000);
            }
          } else {
            setError(error.response?.data || error);
          }
        }
      } finally {
        if (isMounted.current) {
          setLoading(false);
        }
      }
    },
    [path, query]
  );

  const get = useCallback(
    async (req = null) => {
      if (typeof req === 'string') {
        req = { path: req };
      }

      const res = await request({
        ...req,
        params: req?.params || query
      });

      if (!res) return null;
      return res.data || true;
    },
    [request]
  );

  const fetch = useCallback(
    async (params = null) => {
      const res = await get({ params });
      setLocalData(res);
    },
    [get]
  );

  const post = useCallback(
    async (payload, req = null) => {
      if (typeof req === 'string') {
        req = { path: req };
      }
      const res = await request({
        method: 'POST',
        data: payload,
        ...req
      });

      if (!res) return null;
      return res.data || true;
    },
    [request]
  );

  const patch = useCallback(
    async (payload, req = null) => {
      if (typeof req === 'string') {
        req = { path: req };
      }
      const res = await request({
        method: 'PATCH',
        data: payload,
        ...req
      });

      if (!res) return null;
      return res.data || true;
    },
    [request]
  );

  const put = useCallback(
    async (payload, req = null) => {
      if (typeof req === 'string') {
        req = { path: req };
      }
      const res = await request({
        method: 'PUT',
        data: payload,
        ...req
      });

      if (!res) return null;
      return res.data || true;
    },
    [request]
  );

  const del = useCallback(
    async (req = null) => {
      if (typeof req === 'string') {
        req = { path: req };
      }
      const res = await request({
        method: 'DELETE',
        ...req
      });

      if (!res) return false;
      return res.data || true;
    },
    [request]
  );

  const head = useCallback(
    async (req = null) => {
      if (typeof req === 'string') {
        req = { path: req };
      }
      const res = await request({
        method: 'HEAD',
        ...req
      });

      if (!res) return null;
      return res.data || true;
    },
    [request]
  );

  const options = useCallback(
    async (req = null) => {
      if (typeof req === 'string') {
        req = { path: req };
      }
      const res = await request({
        method: 'OPTIONS',
        ...req
      });

      if (!res) return null;
      return res?.data || true;
    },
    [request]
  );

  useEffect(() => {
    isMounted.current = true;
    if (fetchOnMount) fetch();

    return () => {
      isMounted.current = false;
    };
  }, [fetchOnMount, path]);

  return {
    data,
    loading,
    error,
    path,
    query,
    setData: setLocalData,
    setError: setLocalError,
    request,
    fetch,
    get,
    post,
    patch,
    put,
    del,
    head,
    options
  };
}
